import { State } from 'store/types';
import { createSelectorCached } from 'types/types';
import { PageEnum } from './types';
import { createSelector } from 'reselect';
import {
  EngageFlatsDefaultFilter,
  EngagePropertiesDefaultFilter,
} from 'types/Engage';

type RemoteState = Omit<Partial<State>, 'remote'> & Pick<State, 'remote'>;

const selectEngage = createSelectorCached(
  [(state: RemoteState) => state.remote.data, (_, pageKey: string) => pageKey],
  (data, pageKey) => {
    const dataSet = data[pageKey];
    if (!dataSet || dataSet.type !== PageEnum.engage) {
      return undefined;
    }
    return dataSet;
  },
);

export const selectEngagePropertiesIsWaitingForInitLoad = createSelector(
  selectEngage,
  (engage) => !engage || engage.data.isWaitingInitLoad,
);

export const selectEngageIsLoading = createSelector(
  selectEngage,
  (engage) => !!engage?.data.isLoading,
);

/**
 * Properties List
 */

const selectEngageProperties = createSelectorCached(
  selectEngage,
  (engage) => engage?.data.properties,
);

export const selectEngagePropertiesFilters = createSelector(
  selectEngageProperties,
  (properties) => properties?.filters ?? EngagePropertiesDefaultFilter,
);

export const selectEngagePropertiesFilterSearch = createSelector(
  selectEngagePropertiesFilters,
  (filters) => filters.search,
);

export const selectEngagePropertiesFilterSortBy = createSelector(
  selectEngagePropertiesFilters,
  (filters) => filters.sortBy,
);

export const selectEngagePropertiesFilterSortDirection = createSelector(
  selectEngagePropertiesFilters,
  (filters) => filters.sortDirection,
);

const selectEngageFlats = createSelectorCached(
  selectEngage,
  (engage) => engage?.data.flats,
);

export const selectEngageFlatsFilters = createSelector(
  selectEngageFlats,
  (flats) => flats?.filters ?? EngageFlatsDefaultFilter,
);

export const selectEngageFlatsFilterSearch = createSelector(
  selectEngageFlatsFilters,
  (filters) => filters.search,
);

export const selectEngageFlatsFilterOccupationStatuses = createSelector(
  selectEngageFlatsFilters,
  (filters) => filters.occupationStatuses,
);

export const selectEngageFlatsFilterIssueContactTypes = createSelector(
  selectEngageFlatsFilters,
  (filters) => filters.issueContactTypes,
);

export const selectEngageFlatsRequiresResidentSupport = createSelector(
  selectEngageFlatsFilters,
  (filters) => filters.requiresResidentSupport,
);

export const selectEngageFlatsFilterIsHMO = createSelector(
  selectEngageFlatsFilters,
  (filters) => filters.isHMO,
);

export const selectEngageFlatsFilterIsCommerciallyOwned = createSelector(
  selectEngageFlatsFilters,
  (filters) => filters.isCommerciallyOwned,
);

export const selectEngageFlatsEverLoaded = createSelector(
  selectEngage,
  (engage) => !!engage && !engage.data.isWaitingInitLoad,
);

export const selectEngagePropertiesPageNumber = createSelector(
  selectEngageProperties,
  (properties) => properties?.pageNumber || 0,
);

export const selectEngagePropertiesItemIds = createSelector(
  selectEngageProperties,
  (properties) => properties?.dataOrder ?? [],
);

export const selectEngagePropertiesTotalCount = createSelector(
  selectEngageProperties,
  (properties) => properties?.totalCount ?? 0,
);

export const selectEngagePropertiesItem = createSelectorCached(
  [
    (state: RemoteState, pageKey: string, _propertyId: string) =>
      selectEngageProperties(state, pageKey),
    (_, __, propertyId: string) => propertyId,
  ],
  (properties, propertyId) => properties?.data[propertyId],
);

export const selectEngageFlatsPageNumber = createSelector(
  selectEngageFlats,
  (flats) => flats?.pageNumber || 0,
);

export const selectEngageFlatsItemIds = createSelector(
  selectEngageFlats,
  (flats) => flats?.dataOrder ?? [],
);

export const selectEngageFlatsDataMap = createSelector(
  selectEngageFlats,
  (flats) => flats?.data ?? {},
);

export const selectEngageFlatsTotalCount = createSelector(
  selectEngageFlats,
  (flats) => flats?.totalCount ?? 0,
);

export const selectEngageFlatsItem = createSelectorCached(
  [
    (state: RemoteState, pageKey: string, _flatId: string) =>
      selectEngageFlats(state, pageKey),
    (_, __, flatId: string) => flatId,
  ],
  (flats, flatId) => flats?.data[flatId],
);

export const selectEngageFlat = createSelector(
  selectEngage,
  (engage) => engage?.data.flat,
);

export const selectEngageScheduledVisits = createSelector(
  selectEngage,
  (engage) => engage?.data.scheduledVisits,
);

export const selectEngageScheduledVisitTemplates = createSelector(
  selectEngage,
  (engage) => engage?.data.scheduledVisitTemplates,
);

export const selectEngageScheduledVisitTemplatesData = createSelector(
  selectEngageScheduledVisitTemplates,
  (templates) => templates?.templates ?? [],
);

export const selectEngageScheduledVisitTemplatesLoading = createSelector(
  selectEngageScheduledVisitTemplates,
  (templates) => templates?.status ?? 'notloaded',
);

export const selectEngageScheduledVisitsIds = createSelector(
  selectEngageScheduledVisits,
  (scheduledVisits) => scheduledVisits?.dataOrder ?? [],
);

export const selectEngageScheduledVisitItem = createSelectorCached(
  [
    (state: RemoteState, pageKey: string, _selectedVisitId: string) =>
      selectEngageScheduledVisits(state, pageKey),
    (_, __, scheduledVisitsId: string) => scheduledVisitsId,
  ],
  (scheduledVisits, scheduledVisitsId) =>
    scheduledVisits?.data[scheduledVisitsId],
);

const selectEngageScheduledVisitRecipients = createSelectorCached(
  selectEngage,
  (engage) => engage?.data.scheduledVisitRecipients,
);

export const selectEngageScheduledVisitRecipientsData = createSelector(
  selectEngageScheduledVisitRecipients,
  (recipients) => recipients?.data ?? null,
);

export const selectEngageScheduledVisitRecipientsLoading = createSelector(
  selectEngageScheduledVisitRecipients,
  (recipients) => recipients?.loading ?? false,
);
